import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Search, Plus, Minus, MessageCircle, Mail, Phone, Check, Leaf, ShieldCheck, Truck, CreditCard, Users, Globe, Heart } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem, ParallaxImage } from "@/lib/motion";
import { IMG, SEAL } from "@/data/images";
import { useSettings } from "@/context/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { name: "Products", icon: Leaf },
  { name: "Quality & Safety", icon: ShieldCheck },
  { name: "Orders & Shipping", icon: Truck },
  { name: "Payments & Refunds", icon: CreditCard },
  { name: "Partnerships", icon: Users },
  { name: "Sustainability", icon: Globe },
  { name: "Usage & Wellness", icon: Heart },
];

const PROMISE_TICKS = ["Pure Ingredients", "Uncompromising Quality", "Complete Transparency", "Responsible Sourcing", "Safe & Sustainable Packaging", "Customer First"];

const FaqItem = ({ faq, open, onToggle, testId }) => (
  <div className={`border rounded-2xl overflow-hidden transition-colors duration-300 ${open ? "bg-white border-gold/40 shadow-md shadow-forest/5" : "bg-white/70 border-forest/10 hover:border-forest/25"}`}>
    <button data-testid={testId} onClick={onToggle} className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 text-left">
      <span className="font-medium text-forest text-sm sm:text-base">{faq.q}</span>
      <span className={`shrink-0 w-7 h-7 rounded-full border flex items-center justify-center transition-colors ${open ? "bg-forest text-cream border-forest" : "border-forest/20 text-forest"}`}>
        {open ? <Minus size={13} /> : <Plus size={13} />}
      </span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
          <p className="px-5 sm:px-6 pb-5 text-sm text-mutedgreen leading-relaxed">{faq.a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function Faq() {
  const SITE = useSettings();
  const [faqs, setFaqs] = useState([]);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("Products");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/faqs`).then(({ data }) => {
      setFaqs(data);
      if (data.length) setOpenId(data[0].id);
    }).catch(console.error);
  }, []);

  const visible = useMemo(() => {
    let list = faqs;
    if (query.trim()) {
      const n = query.toLowerCase();
      return list.filter((f) => f.q.toLowerCase().includes(n) || f.a.toLowerCase().includes(n));
    }
    return list.filter((f) => f.category === cat);
  }, [faqs, query, cat]);

  const grouped = useMemo(() => {
    if (!query.trim()) return { [cat]: visible };
    return visible.reduce((acc, f) => ({ ...acc, [f.category]: [...(acc[f.category] || []), f] }), {});
  }, [visible, query, cat]);

  return (
    <div data-testid="faq-page">
      <PageHero
        eyebrow="FAQ"
        lines={["Frequently Asked", "Questions"]}
        sub="Questions answered with honesty. If you don't find what you're looking for, our team is always here to help."
        image={IMG.heroFaq}
      >
        <div className="relative max-w-lg">
          <input
            data-testid="faq-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your question here..."
            className="w-full bg-cream text-ink rounded-full pl-6 pr-14 py-4 text-sm shadow-2xl focus:outline-none focus:ring-2 focus:ring-gold placeholder:text-mutedgreen/70"
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-forest text-cream flex items-center justify-center"><Search size={16} /></span>
        </div>
      </PageHero>

      <div data-testid="faq-category-tabs" className="sticky top-[68px] z-30 bg-cream/90 backdrop-blur-xl border-b border-forest/10">
        <div className="container-nb flex gap-2 overflow-x-auto py-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {CATEGORIES.map(({ name, icon: Icon }) => (
            <button
              key={name}
              data-testid={`faq-tab-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => { setCat(name); setQuery(""); }}
              className={`shrink-0 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.1em] px-4 py-2.5 rounded-full border transition-all duration-300 ${cat === name && !query ? "bg-forest text-cream border-forest" : "border-forest/15 text-forest/70 hover:border-gold hover:text-forest"}`}
            >
              <Icon size={13} /> {name}
            </button>
          ))}
        </div>
      </div>

      <section className="py-16 sm:py-20">
        <div className="container-nb grid lg:grid-cols-[1.7fr_1fr] gap-10 items-start">
          <div className="space-y-10">
            {Object.keys(grouped).length === 0 && (
              <p data-testid="faq-no-results" className="text-mutedgreen text-sm">No questions match your search. Try different words, or ask us directly.</p>
            )}
            {Object.entries(grouped).map(([category, items]) => (
              <div key={category}>
                <p className="eyebrow mb-5">{category}</p>
                <div className="space-y-3">
                  {items.map((f) => (
                    <FaqItem
                      key={f.id}
                      faq={f}
                      testId={`faq-item-${f.q.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 40)}`}
                      open={openId === f.id}
                      onToggle={() => setOpenId(openId === f.id ? null : f.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <Stagger className="space-y-6 lg:sticky lg:top-40">
            <StaggerItem>
              <div data-testid="faq-help-card" className="bg-white border border-forest/10 rounded-2xl p-7">
                <h3 className="font-serif text-2xl text-forest">Still Have Questions?</h3>
                <p className="text-xs text-mutedgreen mt-2">We're here to help you with anything you need.</p>
                <div className="mt-6 space-y-5">
                  {[
                    { icon: MessageCircle, title: "Chat With Us", sub: `WhatsApp ${SITE.whatsapp} · ${SITE.hours}` },
                    { icon: Mail, title: "Email Us", sub: `${SITE.email} · replies within 24 hrs` },
                    { icon: Phone, title: "Call Us", sub: `${SITE.phone} · ${SITE.hours}` },
                  ].map(({ icon: Icon, title, sub }) => (
                    <div key={title} className="flex items-center gap-4">
                      <span className="w-11 h-11 rounded-full bg-forest text-cream flex items-center justify-center shrink-0"><Icon size={17} /></span>
                      <div>
                        <p className="text-sm font-semibold text-forest">{title}</p>
                        <p className="text-[11px] text-mutedgreen">{sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div data-testid="faq-promise-card" className="bg-creamdark/60 border border-forest/10 rounded-2xl p-7">
                <h3 className="font-serif text-xl text-forest">Your Trust. Our Promise.</h3>
                <ul className="mt-4 space-y-2.5">
                  {PROMISE_TICKS.map((t) => (
                    <li key={t} className="flex items-center gap-2.5 text-xs text-forest/85"><Check size={13} className="text-gold" /> {t}</li>
                  ))}
                </ul>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="rounded-2xl overflow-hidden relative h-56">
                <ParallaxImage src={IMG.heroStory} alt="Nutribest farms" className="absolute inset-0" speed={8} />
                <div className="absolute inset-0 bg-gradient-to-t from-forestdeep/85 to-transparent" />
                <p className="absolute bottom-5 left-5 right-5 font-serif text-cream text-lg leading-snug">Trust is not a claim. It is a commitment we live by, every day.</p>
              </div>
            </StaggerItem>
          </Stagger>
        </div>
      </section>

      <section className="py-16 bg-creamdark/40 border-t border-forest/10">
        <div className="container-nb flex flex-col md:flex-row items-center gap-8">
          <Reveal className="shrink-0">
            <img src={SEAL} alt="The Nutribest Promise Seal" className="w-28 h-28 object-cover rounded-full mix-blend-multiply" />
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="font-serif text-2xl text-forest">The Nutribest Promise</h3>
            <p className="mt-2 text-sm text-mutedgreen max-w-xl leading-relaxed">Every product we create carries our six uncompromising standards. Because every family deserves food they never have to question.</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
